import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import TarjetasAutocomplete from "../../../components/autocomplete/TarjetasAutocomplete";
let ESTADOS_CACHE = null;
let ESTADOS_PROMISE = null;

export default function FiltroTarjeta({
  campos = [],
  initialValues = {},
  onChange = () => {},
  onSubmit = () => {},
  onClose = () => {},
}) {
  const filtroRef = useRef(null);

  // ====== util ======
  const PAGE_SIZE = 10;
  const dedupBy = (arr, keyFn) => {
    const seen = new Set();
    return arr.filter((it) => {
      const k = keyFn(it);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  // ====== Autocomplete: NRO TARJETA ======
  const [nroQ, setNroQ] = useState("");
  const [sugerencias, setSugerencias] = useState([]);
  const [nroLoading, setNroLoading] = useState(false);
  const [nroOpen, setNroOpen] = useState(false);
  const [nroPage, setNroPage] = useState(0);
  const [nroHasMore, setNroHasMore] = useState(false);
  const [seleccionados, setSeleccionados] = useState([]); // chips nro

  // ====== Autocomplete: PATENTE ======
  const [patenteQ, setPatenteQ] = useState("");
  const [patChipLabel, setPatChipLabel] = useState("");
  const [vehItems, setVehItems] = useState([]);
  const [vehLoading, setVehLoading] = useState(false);
  const [vehOpen, setVehOpen] = useState(false);
  const [vehPage, setVehPage] = useState(0);
  const [vehHasMore, setVehHasMore] = useState(false);

  // ====== Autocomplete: CLIENTE (código) ======
  const [clienteQ, setClienteQ] = useState("");
  const [cliChipLabel, setCliChipLabel] = useState("");
  const [cliItems, setCliItems] = useState([]);
  const [cliLoading, setCliLoading] = useState(false);
  const [cliOpen, setCliOpen] = useState(false);
  const [cliPage, setCliPage] = useState(0);
  const [cliHasMore, setCliHasMore] = useState(false);

  // ====== Autocomplete: EESS ======
  const [eessQ, setEessQ] = useState("");
  const [eessChipLabel, setEessChipLabel] = useState("");
  const [eessItems, setEessItems] = useState([]);
  const [eessLoading, setEessLoading] = useState(false);
  const [eessOpen, setEessOpen] = useState(false);
  const [eessPage, setEessPage] = useState(0);
  const [eessHasMore, setEessHasMore] = useState(false);

  // ====== Form ======
  const [valores, setValores] = useState({});

  // ====== Estados (combo) ======
  const [estadosOpts, setEstadosOpts] = useState(null);

  /* ====== campos por defecto ====== */
  const defaultCampos = useMemo(
    () => [
      {
        name: "numeroTarjeta",
        label: "Nro. Tarjeta",
        tipo: "text",
        placeholder: "Ej: 5454-xxxx-xxxx-1234",
      },
      {
        name: "clienteCodigo",
        label: "Cód. cliente",
        tipo: "text",
        placeholder: "Ej: 8401574",
      },
      {
        name: "codigoEESS",
        label: "Cod. EESS",
        tipo: "text",
        placeholder: "Ej: 00123",
      },
      { name: "estado", label: "Estado", tipo: "select" },
      { name: "fechaAlta", label: "Fecha alta", tipo: "date" },
      { name: "fechaBaja", label: "Fecha baja", tipo: "date" },
      {
        name: "fechaVigenciaInicio",
        label: "Fecha vigencia Inicio",
        tipo: "date",
      },
      { name: "fechaVigenciaFin", label: "Fecha vigencia Fin", tipo: "date" },
    ],
    [],
  );

  const combinados = useMemo(
    () => [...defaultCampos, ...(campos || [])],
    [defaultCampos, campos],
  );
  const camposUnicos = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of combinados)
      if (c?.name && !seen.has(c.name)) {
        seen.add(c.name);
        out.push(c);
      }
    return out;
  }, [combinados]);

  // quitamos “Cliente” sobrantes
  const excludedNameSet = new Set([
    "cliente",
    "clienteNombre",
    "clienteCodigoNombre",
    "codigoNombreCliente",
    "clienteBusqueda",
    "clienteFiltro",
  ]);
  const camposSinClienteExtra = useMemo(() => {
    return camposUnicos.filter((c) => {
      if (!c) return false;
      if (excludedNameSet.has(c.name)) return false;
      if (
        typeof c.label === "string" &&
        c.label.trim().toLowerCase() === "cliente"
      )
        return false;
      return true;
    });
  }, [camposUnicos]);

  const groupedCodeNames = new Set(["clienteCodigo", "codigoEESS"]);
  const groupedDateNames = new Set([
    "fechaAlta",
    "fechaBaja",
    "fechaVigenciaInicio",
    "fechaVigenciaFin",
  ]);
  const topNames = new Set(["numeroTarjeta", "estado", "vehiculosConTarjeta", "centroCosto"]);

  const cfgEstado = useMemo(
    () => camposSinClienteExtra.find((c) => c.name === "estado"),
    [camposSinClienteExtra],
  );
  const camposParaMapear = useMemo(
    () =>
      camposSinClienteExtra.filter(
        (c) =>
          !topNames.has(c.name) &&
          !groupedCodeNames.has(c.name) &&
          !groupedDateNames.has(c.name),
      ),
    [camposSinClienteExtra],
  );

  /* ====== Rehidratación ====== */
  useEffect(() => {
    const iv = { ...initialValues };
    if (
      !iv.fechaAlta &&
      iv.fechaAltaDesde &&
      iv.fechaAltaDesde === iv.fechaAltaHasta
    ) {
      iv.fechaAlta = iv.fechaAltaDesde;
    }

    setValores((prev) => ({ ...prev, ...iv }));

    const chips = Array.isArray(iv.numeroTarjeta)
      ? iv.numeroTarjeta
      : iv.numero
        ? [iv.numero]
        : [];
    if (chips.length)
      setSeleccionados(chips.filter(Boolean).map((n) => ({ numero: n })));

    setPatChipLabel(iv.vehiculoEtiqueta || "");
    setPatenteQ("");
    setCliChipLabel(iv.clienteEtiqueta || iv.clienteCodigo || "");
    setClienteQ("");
    setEessChipLabel(iv.eessEtiqueta || iv.codigoEESS || "");
    setEessQ("");
  }, [initialValues]);

  /* ====== Estados (una sola vez) ====== */
  useEffect(() => {
    let mounted = true;
    async function ensureEstados() {
      if (ESTADOS_CACHE) {
        mounted && setEstadosOpts(ESTADOS_CACHE);
        return;
      }
      if (ESTADOS_PROMISE) {
        try {
          mounted && setEstadosOpts(await ESTADOS_PROMISE);
        } catch {
          mounted && setEstadosOpts([{ value: "", label: "Todos" }]);
        }
        return;
      }

      ESTADOS_PROMISE = (async () => {
        const raw = await apiFetchJson(apiRoutes.tarjetas.estados);
        const arr = Array.isArray(raw?.items)
          ? raw.items
          : Array.isArray(raw)
            ? raw
            : [];
        const opts = [
          { value: "", label: "Todos" },
          ...arr
            .map((e) => {
              const id =
                e?.ESTA_SQ_COD ?? e?.esta_sq_cod ?? e?.id ?? e?.value ?? "";
              const nombre =
                e?.ESTA_NM_ESTADO ??
                e?.esta_nm_estado ??
                e?.nombre ??
                e?.label ??
                "";
              return { value: String(id ?? ""), label: String(nombre ?? "") };
            })
            .filter((o) => o.label),
        ];
        ESTADOS_CACHE = opts.length ? opts : [{ value: "", label: "Todos" }];
        return ESTADOS_CACHE;
      })();

      try {
        mounted && setEstadosOpts(await ESTADOS_PROMISE);
      } catch {
        mounted && setEstadosOpts([{ value: "", label: "Todos" }]);
      } finally {
        ESTADOS_PROMISE = null;
      }
    }
    ensureEstados();
    return () => {
      mounted = false;
    };
  }, []);

  /* ====== Dropdown control ====== */
  const closeDropdowns = (except = null) => {
    if (except !== "numero") setNroOpen(false);
    if (except !== "vehiculo") setVehOpen(false);
    if (except !== "cliente") setCliOpen(false);
    if (except !== "eess") setEessOpen(false);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Escape") closeDropdowns(null);
  };

  /* ====== Fetchers ====== */
  const fetchNroPage = useCallback(async (term, page, append = false) => {
    try {
      setNroLoading(true);
      const data = await apiFetchJson(
        apiRoutes.tarjetas.autocomplete("numero", term, page, PAGE_SIZE),
      );
      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];
      setSugerencias((prev) =>
        append
          ? dedupBy(
              [...prev, ...items],
              (it) => it.numero ?? it.id ?? JSON.stringify(it),
            )
          : items,
      );
      setNroOpen(true);
      const hasMore = Boolean(data?.hasMore) || items.length === PAGE_SIZE;
      setNroHasMore(hasMore);
      setNroPage(page);
    } catch {
      setNroOpen(false);
    } finally {
      setNroLoading(false);
    }
  }, []);

  const fetchVehiculoPage = useCallback(async (term, page, append = false) => {
    try {
      setVehLoading(true);
      const data = await apiFetchJson(
        apiRoutes.tarjetas.autocomplete("vehiculo", term, page, PAGE_SIZE),
      );
      const raw = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];
      const norm = raw.map((it) => {
        const id = it?.id ?? it?.vehiculoId ?? it?.VEHI_SQ_COD;
        const patente =
          it?.patente ??
          it?.VEHI_DS_PATENTE ??
          it?.descricaoPatente ??
          it?.placa;
        const marca =
          it?.marcaNombre ??
          it?.MARC_DS_MARCA ??
          it?.marca?.nombre ??
          it?.marca;
        const modelo =
          it?.modelo ??
          it?.VEHI_DS_DETALLES ??
          it?.descricaoDetalles ??
          it?.detalles;
        const etiqueta =
          it?.etiqueta ||
          [patente, [marca, modelo].filter(Boolean).join(" ")]
            .filter(Boolean)
            .join(" — ");
        return { id, etiqueta };
      });
      setVehItems((prev) =>
        append
          ? dedupBy([...prev, ...norm], (it) => it.id ?? it.etiqueta)
          : norm,
      );
      setVehOpen(true);
      const hasMore = Boolean(data?.hasMore) || norm.length === PAGE_SIZE;
      setVehHasMore(hasMore);
      setVehPage(page);
    } catch {
      setVehOpen(false);
    } finally {
      setVehLoading(false);
    }
  }, []);

  const fetchClientePage = useCallback(async (term, page, append = false) => {
    try {
      setCliLoading(true);
      const data = await apiFetchJson(
        apiRoutes.tarjetas.autocomplete("cliente", term, page, PAGE_SIZE),
      );
      const raw = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];
      const norm = raw
        .map((it) => {
          const codigo = it?.codigo ?? it?.clienteCodigo ?? it?.code;
          const nombre = it?.nombre ?? it?.clienteNombre ?? it?.label;
          return {
            codigo,
            nombre,
            etiqueta: [codigo, nombre].filter(Boolean).join(" — "),
          };
        })
        .filter((it) => it.codigo);
      setCliItems((prev) =>
        append ? dedupBy([...prev, ...norm], (it) => it.codigo) : norm,
      );
      setCliOpen(true);
      const hasMore = Boolean(data?.hasMore) || norm.length === PAGE_SIZE;
      setCliHasMore(hasMore);
      setCliPage(page);
    } catch {
      setCliOpen(false);
    } finally {
      setCliLoading(false);
    }
  }, []);

  const fetchEessPage = useCallback(async (term, page, append = false) => {
    try {
      setEessLoading(true);
      const data = await apiFetchJson(
        apiRoutes.tarjetas.autocomplete("eess", term, page, PAGE_SIZE),
      );
      const raw = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];
      const norm = raw
        .map((it) => {
          const codigo = it?.codigo ?? it?.eessCodigoEstacion ?? it?.code;
          const nombre = it?.nombre ?? it?.eessNombre ?? it?.label;
          return {
            codigo,
            nombre,
            etiqueta: [codigo, nombre].filter(Boolean).join(" — "),
          };
        })
        .filter((it) => it.codigo);
      setEessItems((prev) =>
        append ? dedupBy([...prev, ...norm], (it) => it.codigo) : norm,
      );
      setEessOpen(true);
      const hasMore = Boolean(data?.hasMore) || norm.length === PAGE_SIZE;
      setEessHasMore(hasMore);
      setEessPage(page);
    } catch {
      setEessOpen(false);
    } finally {
      setEessLoading(false);
    }
  }, []);

  /* ====== Debounce (>= 2 chars) ====== */
  useEffect(() => {
    const t = setTimeout(() => {
      if ((nroQ || "").trim().length >= 2) {
        setSugerencias([]);
        setNroPage(0);
        setNroHasMore(false);
        fetchNroPage(nroQ.trim(), 0, false);
      } else {
        setSugerencias([]);
        setNroOpen(false);
        setNroPage(0);
        setNroHasMore(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [nroQ, fetchNroPage]);

  useEffect(() => {
    const t = setTimeout(() => {
      if ((patenteQ || "").trim().length >= 2) {
        setVehItems([]);
        setVehPage(0);
        setVehHasMore(false);
        fetchVehiculoPage(patenteQ.trim(), 0, false);
      } else {
        setVehItems([]);
        setVehOpen(false);
        setVehPage(0);
        setVehHasMore(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [patenteQ, fetchVehiculoPage]);

  useEffect(() => {
    const t = setTimeout(() => {
      if ((clienteQ || "").trim().length >= 2) {
        setCliItems([]);
        setCliPage(0);
        setCliHasMore(false);
        fetchClientePage(clienteQ.trim(), 0, false);
      } else {
        setCliItems([]);
        setCliOpen(false);
        setCliPage(0);
        setCliHasMore(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [clienteQ, fetchClientePage]);

  useEffect(() => {
    const t = setTimeout(() => {
      if ((eessQ || "").trim().length >= 2) {
        setEessItems([]);
        setEessPage(0);
        setEessHasMore(false);
        fetchEessPage(eessQ.trim(), 0, false);
      } else {
        setEessItems([]);
        setEessOpen(false);
        setEessPage(0);
        setEessHasMore(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [eessQ, fetchEessPage]);

  /* ====== Scroll infinito ====== */
  const handleNroScroll = (e) => {
    if (!nroHasMore || nroLoading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchNroPage(nroQ.trim(), nroPage + 1, true);
    }
  };
  const handleVehScroll = (e) => {
    if (!vehHasMore || vehLoading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchVehiculoPage(patenteQ.trim(), vehPage + 1, true);
    }
  };
  const handleCliScroll = (e) => {
    if (!cliHasMore || cliLoading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchClientePage(clienteQ.trim(), cliPage + 1, true);
    }
  };
  const handleEessScroll = (e) => {
    if (!eessHasMore || eessLoading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchEessPage(eessQ.trim(), eessPage + 1, true);
    }
  };

  // ====== helpers ======
  const nonEmpty = (v) =>
    v !== undefined && v !== null && String(v).trim() !== "";
  const hasFilters = (f) => {
    const arrNonEmpty = (a) => Array.isArray(a) && a.some(nonEmpty);
    return (
      arrNonEmpty(f.numeroTarjeta) ||
      [
        "clienteCodigo",
        "codigoEESS",
        "estado",
        "vehiculoId",
        "fechaAlta",
        "fechaBaja",
        "fechaVigenciaInicio",
        "fechaVigenciaFin",
      ].some((k) => nonEmpty(f[k]))
    );
  };

  // snapshot para UI
  const buildSubmitSnapshot = () => {
    const snap = {
      ...valores,
      numeroTarjeta: seleccionados.map((s) => s.numero),
      vehiculoEtiqueta: patChipLabel || undefined,
      clienteEtiqueta: cliChipLabel || undefined,
      eessEtiqueta: eessChipLabel || undefined,
      fechaAlta: valores.fechaAlta || "",
    };
    return { ...snap, __hasFilters: hasFilters(snap) };
  };

  // 👇 Devolvemos LAS MISMAS KEYS que usa ListaTarjetas (no traducimos nombres aquí)
  const mapToBackendParams = (snap) => {
    const q = {};
    const nonEmpty = (v) =>
      v !== undefined && v !== null && String(v).trim() !== "";

    if (Array.isArray(snap.numeroTarjeta) && snap.numeroTarjeta.length)
      q.numeroTarjeta = snap.numeroTarjeta;
    if (nonEmpty(snap.estado)) q.estado = snap.estado;
    if (nonEmpty(snap.codigoEESS)) q.codigoEESS = snap.codigoEESS;
    if (nonEmpty(snap.clienteCodigo)) q.clienteCodigo = snap.clienteCodigo;
    if (nonEmpty(snap.vehiculoId)) q.vehiculoId = snap.vehiculoId;

    if (nonEmpty(snap.fechaAlta)) q.fechaAlta = snap.fechaAlta;
    if (nonEmpty(snap.fechaBaja)) q.fechaBaja = snap.fechaBaja;
    if (nonEmpty(snap.fechaVigenciaInicio))
      q.fechaVigenciaInicio = snap.fechaVigenciaInicio;
    if (nonEmpty(snap.fechaVigenciaFin))
      q.fechaVigenciaFin = snap.fechaVigenciaFin;
    //Nuevo
    if (nonEmpty(snap.centroCosto)) q.centroCosto = snap.centroCosto.trim();

    return q;
  };

  // snapshot “en vivo” (para onChange al limpiar chips)
  const buildLiveSnapshot = () => ({
    ...valores,
    numeroTarjeta: seleccionados.map((s) => s.numero),
    vehiculoEtiqueta: patChipLabel || undefined,
    clienteEtiqueta: cliChipLabel || undefined,
    eessEtiqueta: eessChipLabel || undefined,
  });

  /* ====== Handlers ====== */
  const handleSelectNro = (item) => {
    const numero = (item?.numero || "").replace(/-+$/, "");
    if (!numero) return;
    if (!seleccionados.some((s) => s.numero === numero)) {
      setSeleccionados((prev) => [...prev, { numero }]);
    }
    setNroQ("");
    setNroOpen(false);
    setSugerencias([]);
  };
  const handleRemoveNro = (numero) => {
    const next = seleccionados.filter((s) => s.numero !== numero);
    setSeleccionados(next);
    onChange({
      ...buildLiveSnapshot(),
      numeroTarjeta: next.map((s) => s.numero),
    });
  };

  const handleVehiculoSelect = (item) => {
    if (!item?.id) return;
    setValores((p) => ({ ...p, vehiculoId: String(item.id) }));
    setPatChipLabel(item.etiqueta || "");
    setPatenteQ("");
    setVehOpen(false);
    setVehItems([]);
  };
  const clearVehiculo = () => {
    const next = { ...valores, vehiculoId: "" };
    setValores(next);
    setPatChipLabel("");
    setPatenteQ("");
    setVehItems([]);
    setVehOpen(false);
    setVehPage(0);
    setVehHasMore(false);
    onChange({
      ...buildLiveSnapshot(),
      vehiculoId: "",
      vehiculoEtiqueta: undefined,
    });
  };

  const handleClienteSelect = (item) => {
    if (!item?.codigo) return;
    setValores((p) => ({ ...p, clienteCodigo: String(item.codigo) }));
    setCliChipLabel(item.etiqueta || item.codigo || "");
    setClienteQ("");
    setCliOpen(false);
    setCliItems([]);
  };
  const clearCliente = () => {
    const next = { ...valores, clienteCodigo: "" };
    setValores(next);
    setCliChipLabel("");
    setClienteQ("");
    setCliItems([]);
    setCliOpen(false);
    setCliPage(0);
    setCliHasMore(false);
    onChange({
      ...buildLiveSnapshot(),
      clienteCodigo: "",
      clienteEtiqueta: undefined,
    });
  };

  const handleEessSelect = (item) => {
    if (!item?.codigo) return;
    setValores((p) => ({ ...p, codigoEESS: String(item.codigo) }));
    setEessChipLabel(item.etiqueta || item.codigo || "");
    setEessQ("");
    setEessOpen(false);
    setEessItems([]);
  };
  const clearEess = () => {
    const next = { ...valores, codigoEESS: "" };
    setValores(next);
    setEessChipLabel("");
    setEessQ("");
    setEessItems([]);
    setEessOpen(false);
    setEessPage(0);
    setEessHasMore(false);
    onChange({
      ...buildLiveSnapshot(),
      codigoEESS: "",
      eessEtiqueta: undefined,
    });
  };

  const handleSimpleChange = (e) => {
    const { name, value } = e.target;
    setValores((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearAll = () => {
    setValores({});
    setSeleccionados([]);
    setNroQ("");
    setPatenteQ("");
    setPatChipLabel("");
    setVehItems([]);
    setVehOpen(false);
    setVehPage(0);
    setVehHasMore(false);
    setClienteQ("");
    setCliChipLabel("");
    setCliItems([]);
    setCliOpen(false);
    setCliPage(0);
    setCliHasMore(false);
    setEessQ("");
    setEessChipLabel("");
    setEessItems([]);
    setEessOpen(false);
    setEessPage(0);
    setEessHasMore(false);

    onChange({});
    onSubmit({
      numeroTarjeta: [],
      clienteCodigo: "",
      estado: "",
      vehiculoId: "",
      vehiculoEtiqueta: "",
      codigoEESS: "",
      fechaAlta: "",
      fechaBaja: "",
      fechaVigenciaInicio: "",
      fechaVigenciaFin: "",
    }); // vacío
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const uiSnap = buildSubmitSnapshot();
    const params = mapToBackendParams(uiSnap);
    onSubmit(params); // <— mandamos las mismas keys que maneja el padre
  };

  /* ====== cerrar panel al click fuera ====== */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtroRef.current && !filtroRef.current.contains(event.target))
        onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  /* ====== evitar scroll body ====== */
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const estadoOptions = useMemo(
    () => cfgEstado?.opciones || estadosOpts || [{ value: "", label: "Todos" }],
    [cfgEstado, estadosOpts],
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end">
      <div
        ref={filtroRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md h-full bg-white shadow-xl p-6 overflow-auto"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-title">Filtros</h2>
          <button
            onClick={onClose}
            className="btn-cancel"
            title="Cerrar"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
          {/* Nro. Tarjeta */}
          {/* Nro. Tarjeta */}
<div>
  <label className="form-label-fil">Nro. Tarjeta</label>

  <TarjetasAutocomplete
    value={null}                 // no usamos selección única
    tipo="numero"
    disabled={false}
    placeholder="Buscar tarjeta..."
    onChange={(tarjeta) => {
      if (!tarjeta?.numero) return;

      if (!seleccionados.some((s) => s.numero === tarjeta.numero)) {
        const next = [...seleccionados, { numero: tarjeta.numero }];
        setSeleccionados(next);

        onChange({
          ...buildLiveSnapshot(),
          numeroTarjeta: next.map((s) => s.numero),
        });
      }
    }}
  />

  {/* Chips */}
  <div className="flex flex-wrap mt-1 gap-1">
    {seleccionados.map((s) => (
      <span
        key={s.numero}
        className="bg px-2 py-1 rounded-full text-sm flex items-center gap-1"
      >
        {s.numero}
        <button
          type="button"
          onClick={() => handleRemoveNro(s.numero)}
        >
          &times;
        </button>
      </span>
    ))}
  </div>
</div>


          {/* N° de chapa */}
          <div style={{ position: "relative" }}>
            <label className="form-label-fil">
              N° de chapa
            </label>
            <input
              type="text"
              placeholder="Escribí al menos 2 caracteres..."
              value={patenteQ}
              onChange={(e) => {
                setPatenteQ(e.target.value);
                closeDropdowns("vehiculo");
                if (valores.vehiculoId)
                  setValores((p) => ({ ...p, vehiculoId: "" }));
              }}
              className="input"
              autoComplete="off"
              onFocus={() => {
                if (vehItems.length) setVehOpen(true);
                closeDropdowns("vehiculo");
              }}
              onKeyDown={handleKeyDown}
            />
            {valores.vehiculoId && patChipLabel && (
              <div className="flex flex-wrap mt-1 gap-1">
                <span className="bg px-2 py-1 rounded-full text-sm flex items-center gap-1">
                  {patChipLabel}
                  <button type="button" onClick={clearVehiculo}>
                    &times;
                  </button>
                </span>
              </div>
            )}
            <input
              type="hidden"
              name="vehiculoId"
              value={valores.vehiculoId || ""}
            />

            {vehOpen && (
              <div
                onScroll={handleVehScroll}
                className="absolute z-10 bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto w-full"
              >
                {vehItems.map((it, i) => (
                  <div
                    key={`${it.id}-${i}`}
                    onClick={() => handleVehiculoSelect(it)}
                    className="p-2 cursor-pointer hover"
                  >
                    {it.etiqueta || "—"}
                  </div>
                ))}
                {vehLoading && (
                  <div className="p-2 text-sm text-gray-500">Cargando...</div>
                )}
                {!vehLoading && vehHasMore && (
                  <div className="p-2 text-xs text-gray-400 text-center">
                    Desplazá para ver más…
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Estado */}
          <div>
            <label className="form-label-fil">
              {cfgEstado?.label || "Estado"}
            </label>
            <select
              name="estado"
              value={valores.estado || ""}
              onChange={handleSimpleChange}
              className="input"
              onKeyDown={handleKeyDown}
            >
              {estadoOptions.map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Ctro. Costo */}
<div>
  <label className="form-label-fil">Ctro. Costo</label>
  <input
    type="text"
    name="centroCosto"
    placeholder="Filtrar por centro de costo..."
    value={valores.centroCosto || ""}
    onChange={handleSimpleChange}
    className="input"
    autoComplete="off"
    onKeyDown={handleKeyDown}
  />
</div>

          {/* Códigos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Cliente */}
            <div style={{ position: "relative" }}>
              <label className="form-label-fil">
                Código Cliente
              </label>
              <input
                type="text"
                placeholder="Escribí al menos 2 caracteres..."
                value={clienteQ}
                onChange={(e) => {
                  setClienteQ(e.target.value);
                  closeDropdowns("cliente");
                  if (valores.clienteCodigo)
                    setValores((p) => ({ ...p, clienteCodigo: "" }));
                }}
                className="input"
                autoComplete="off"
                onFocus={() => {
                  if (cliItems.length) setCliOpen(true);
                  closeDropdowns("cliente");
                }}
                onKeyDown={handleKeyDown}
              />
              {valores.clienteCodigo && cliChipLabel && (
                <div className="flex flex-wrap mt-1 gap-1">
                  <span className="bg px-2 py-1 rounded-full text-sm flex items-center gap-1">
                    {cliChipLabel}
                    <button type="button" onClick={clearCliente}>
                      &times;
                    </button>
                  </span>
                </div>
              )}

              {cliOpen && (
                <div
                  onScroll={handleCliScroll}
                  className="absolute z-10 bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto w-full"
                >
                  {cliItems.map((it, i) => (
                    <div
                      key={`${it.codigo}-${i}`}
                      onClick={() => handleClienteSelect(it)}
                      className="p-2 cursor-pointer hover"
                    >
                      {it.etiqueta}
                    </div>
                  ))}
                  {cliLoading && (
                    <div className="p-2 text-sm text-gray-500">Cargando...</div>
                  )}
                  {!cliLoading && cliHasMore && (
                    <div className="p-2 text-xs text-gray-400 text-center">
                      Desplazá para ver más…
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* EESS */}
            <div style={{ position: "relative" }}>
              <label className="form-label-fil">
                Código EESS
              </label>
              <input
                type="text"
                placeholder="Escribí al menos 2 caracteres..."
                value={eessQ}
                onChange={(e) => {
                  setEessQ(e.target.value);
                  closeDropdowns("eess");
                  if (valores.codigoEESS)
                    setValores((p) => ({ ...p, codigoEESS: "" }));
                }}
                className="input"
                autoComplete="off"
                onFocus={() => {
                  if (eessItems.length) setEessOpen(true);
                  closeDropdowns("eess");
                }}
                onKeyDown={handleKeyDown}
              />
              {valores.codigoEESS && eessChipLabel && (
                <div className="flex flex-wrap mt-1 gap-1">
                  <span className="bg px-2 py-1 rounded-full text-sm flex items-center gap-1">
                    {eessChipLabel}
                    <button type="button" onClick={clearEess}>
                      &times;
                    </button>
                  </span>
                </div>
              )}

              {eessOpen && (
                <div
                  onScroll={handleEessScroll}
                  className="absolute z-10 bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto w-full"
                >
                  {eessItems.map((it, i) => (
                    <div
                      key={`${it.codigo}-${i}`}
                      onClick={() => handleEessSelect(it)}
                      className="p-2 cursor-pointer hover"
                    >
                      {it.etiqueta}
                    </div>
                  ))}
                  {eessLoading && (
                    <div className="p-2 text-sm text-gray-500">Cargando...</div>
                  )}
                  {!eessLoading && eessHasMore && (
                    <div className="p-2 text-xs text-gray-400 text-center">
                      Desplazá para ver más…
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="form-label-fil">
                Fecha Alta
              </label>
              <input
  type="date"
  name="fechaAlta"
  value={valores.fechaAlta || ""}
  onChange={(e) => setValores(p => ({ ...p, fechaAlta: e.target.value }))}
  className="input"
/>
            </div>
            <div>
              <label className="form-label-fil">
                Fecha Baja
              </label>
              <input
  type="date"
  name="fechaBaja"
  value={valores.fechaBaja || ""}
  onChange={(e) => setValores(p => ({ ...p, fechaBaja: e.target.value }))}
  className="input"
/>
            </div>
          </div>
         <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
  <div>
    <label className="form-label-fil"> Inicio de vigencia</label>
    <input
      type="date"
      name="fechaVigenciaInicio"
      value={valores.fechaVigenciaInicio || ""}
      onChange={(e) => setValores(p => ({ ...p, fechaVigenciaInicio: e.target.value }))}
      className="input"
    />
  </div>
  <div>
    <label className="form-label-fil">Fin de vigencia</label>
    <input
      type="date"
      name="fechaVigenciaFin"
      value={valores.fechaVigenciaFin || ""}
      onChange={(e) => setValores(p => ({ ...p, fechaVigenciaFin: e.target.value }))}
      className="input"
    />
  </div>
</div>

          {/* Extras por props */}
          {camposParaMapear.map((campo) => (
            <div key={campo.name}>
              <label className="block text-sm font-medium text-gray-700">
                {campo.label}
              </label>
              {campo.tipo === "select" ? (
                <select
                  name={campo.name}
                  value={valores[campo.name] || ""}
                  onChange={handleSimpleChange}
                  className="input"
                  onKeyDown={handleKeyDown}
                >
                  {(campo.opciones || []).map((opt) => (
                    <option key={String(opt.value)} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={campo.tipo}
                  name={campo.name}
                  placeholder={campo.placeholder}
                  value={valores[campo.name] || ""}
                  onChange={handleSimpleChange}
                  autoComplete="off"
                  className="input"
                  onKeyDown={handleKeyDown}
                />
              )}
            </div>
          ))}

          <div className="text-right">
            {/* <button
              type="button"
              className="btn mt-2 mr-2"
              onClick={handleClearAll}
            >
              Limpiar filtros
            </button> */}
            <button type="submit" className="btn">
              Aplicar Filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
